import React, {
  RefObject,
  ReactNode,
  useLayoutEffect,
} from 'react';
import clsx from 'clsx';
import {
  useFileDropzone,
} from 'pages/Dashboard/pages/Billing/hooks';
import UploadIcon from 'core/icons/upload';
import {
  Accept,
  DropEvent,
  FileRejection,
} from 'react-dropzone';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
} from '@populateui/utils';

export type OnDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;

export type DropzoneHandle = {
  open: () => void;
};

type Part = 'root' | 'icon' | 'text';
type Props = {
  onDrop?: OnDrop;
  placeholder?: string | ReactNode;
  rejectPlaceholder?: string | ReactNode;
  rejectMultiplePlaceholder?: string | ReactNode;
  classes?: Partial<Record<Part, string>>;
  accept?: Accept;
  icon?: ReactNode;
  disabled?: boolean;
  multiple?: boolean;
  openRef?: RefObject<DropzoneHandle | null>;
};

export default function Dropzone({
  onDrop,
  placeholder,
  rejectPlaceholder,
  rejectMultiplePlaceholder,
  classes,
  accept,
  icon,
  disabled = false,
  multiple = false,
  openRef,
}: Props) {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    isMultipleDrag,
    open,
  } = useFileDropzone(onDrop, accept, multiple, disabled);

  useLayoutEffect(() => {
    if (!isNil(openRef)) {
      openRef.current = { open };
    }
    return () => {
      if (!isNil(openRef)) {
        openRef.current = null;
      }
    };
  }, [open, openRef]);

  let effectivePlaceholder: string | ReactNode | undefined = placeholder;
  if (isDragReject) {
    if (isMultipleDrag && !isDragAccept) {
      effectivePlaceholder = rejectMultiplePlaceholder ?? 'Multiple files are not supported';
    } else {
      effectivePlaceholder = rejectPlaceholder ?? 'File type not supported';
    }
  }

  return (
    <div
      {...getRootProps({ className: 'dropzone' })}
      className={clsx(
        'w-full box-border flex flex-col items-center justify-center gap-2 rounded border border-dashed border-blue-500 transition-all',
        'p-6',
        { '!flex cursor-pointer bg-blue-50 hover:bg-blue-100 hover:border-blue-400': isDragActive || isDragReject },
        { 'cursor-pointer bg-blue-50 hover:bg-blue-100 hover:border-blue-400': !disabled && !isDragActive && !isDragReject },
        { 'opacity-60 !cursor-not-allowed bg-blue-50': disabled },
        { 'ring-2 ring-blue-400 bg-blue-100 border-blue-400': isDragActive },
        { 'ring-2 ring-green-400 bg-green-50': isDragAccept },
        { 'ring-2 ring-red-400 bg-red-50': isDragReject },
        classes?.root,
      )}
    >
      <input {...getInputProps()} disabled={disabled} />
      {isNil(icon)
        ? <UploadIcon className={clsx('text-blue-500', classes?.icon)} />
        : icon}
      {!isEmptyString(effectivePlaceholder) && (
        <span className={clsx('text-xs text-blue-700 font-medium whitespace-pre-line text-center', classes?.text)}>
          {effectivePlaceholder}
        </span>
      )}
    </div>
  );
}
